import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { decodeIdToken, type GuideClaims } from "../lib/cognito";
import { registerAuth } from "../lib/api";
import { deviceLabel, registerSession } from "../lib/session-api";

// guide-web 認証状態の単一の出所（guide-app の AuthContext と同型）。
// IdToken を localStorage に保持し、リロード後も exp までセッションを復元する。
// 認可自体は API GW の JWT authorizer が担うので、ここでの claims は表示・ルーティング用途。

const STORAGE_KEY = "tst.guideweb.idToken";
// F-GUIDE-032（tst-jjcr）。サインイン成功時に自己登録した guide_session.id。
// x-session-id として api.ts が自動付与し、session-guard の端末単位失効判定を有効化する。
const SESSION_STORAGE_KEY = "tst.guideweb.sessionId";

type AuthState = {
  token: string | null;
  guide: GuideClaims | null;
  isAuthenticated: boolean;
  /** 起動時のセッション復元が完了したか（false の間はスプラッシュ表示）。 */
  ready: boolean;
  signIn: (idToken: string) => void;
  signOut: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

/** exp（epoch秒）を過ぎていないか。60秒のスキューを引いて早めに失効扱い。 */
function isLive(claims: GuideClaims | null): claims is GuideClaims {
  if (!claims) return false;
  return claims.exp * 1000 - 60_000 > Date.now();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [guide, setGuide] = useState<GuideClaims | null>(null);
  const [ready, setReady] = useState(false);
  const expiryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SESSION_STORAGE_KEY);
    if (expiryTimer.current) clearTimeout(expiryTimer.current);
    setToken(null);
    setGuide(null);
  }, []);

  // exp の到来でローカルセッションを自動失効させる（手動 401 を待たずにサインイン画面へ）。
  const scheduleExpiry = useCallback(
    (claims: GuideClaims) => {
      if (expiryTimer.current) clearTimeout(expiryTimer.current);
      const ms = Math.max(0, claims.exp * 1000 - 60_000 - Date.now());
      expiryTimer.current = setTimeout(signOut, Math.min(ms, 2_147_483_000));
    },
    [signOut],
  );

  const signIn = useCallback(
    (idToken: string) => {
      const claims = decodeIdToken(idToken);
      if (!isLive(claims)) return; // 失効済みトークンは確立しない
      localStorage.setItem(STORAGE_KEY, idToken);
      setToken(idToken);
      setGuide(claims);
      scheduleExpiry(claims);
      // F-GUIDE-032: この端末のセッションを自己登録し、x-session-id で端末単位失効判定を有効化する。
      // best-effort — 失敗してもサインイン自体は成立させる（全端末一括失効の判定は残る）。
      registerSession(deviceLabel())
        .then((r) => localStorage.setItem(SESSION_STORAGE_KEY, r.id))
        .catch((e) => console.error("[auth] registerSession failed (non-fatal):", e));
    },
    [scheduleExpiry],
  );

  // 起動時: 保存済みトークンを復元（exp 切れなら破棄）。
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const claims = decodeIdToken(saved);
      if (isLive(claims)) {
        setToken(saved);
        setGuide(claims);
        scheduleExpiry(claims);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setReady(true);
    return () => {
      if (expiryTimer.current) clearTimeout(expiryTimer.current);
    };
  }, [scheduleExpiry]);

  // api クライアントへトークンゲッタと 401 ハンドラを供給。getToken は localStorage（トークンの
  // 正本）を直接読む（復元直後の初回レンダリングでの競合回避。guide-app tst-sg7.2 で確立）。
  useEffect(() => {
    registerAuth({
      getToken: () => localStorage.getItem(STORAGE_KEY),
      getSessionId: () => localStorage.getItem(SESSION_STORAGE_KEY),
      onUnauthorized: signOut,
    });
  }, [signOut]);

  const value = useMemo<AuthState>(
    () => ({ token, guide, isAuthenticated: token != null, ready, signIn, signOut }),
    [token, guide, ready, signIn, signOut],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
